// @ts-check
// Página Agenda (/calendar): mês, semana e dia com os eventos da equipe, as datas de visita, sondagem, vivência,
// prova de bolsa e open school dos cards e os eventos da escola (contrato 08 da agenda, seção 5). A visão, a data e
// os filtros ficam na URL, para o link abrir a mesma agenda.
import { html } from 'htm/preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { CALENDAR_EVENT_TYPES } from '../../../../shared/calendar/event-types.js';
import { searchParamsState, updateSearchParams } from '../../app/router.js';
import { listCalendarEvents } from '../../data/calendar.js';
import { loadWorkspaceMembers, workspaceMembersState } from '../../data/workspace-members.js';
import { CALENDAR_VIEWS, formatRangeTitle, fromDateKey, getVisibleRange, shiftAnchor, toDateKey } from '../../lib/calendar-dates.js';
import { formatFullName } from '../../lib/field-metadata.js';
import { getTagStyle } from '../../lib/theme-colors.js';
import { PageCard, PageHeader } from '../page/page-card.js';
import { Button, LightIconButton } from '../../ui/button.js';
import { Toggle } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { showErrorSnackbar } from '../../ui/snackbar.js';
import { CalendarEventDetail } from './calendar-event-detail.js';
import { MonthView, TimeGridView } from './calendar-views.js';
import { ScheduleEventModal } from './schedule-event-modal.js';
import { t } from '../../i18n/index.js';

/** @typedef {import('../../data/calendar.js').CalendarItem} CalendarItem */

// legenda e filtro: os tipos do CRM e os eventos da escola
const LEGEND_TYPES = [...CALENDAR_EVENT_TYPES.map((type) => ({ key: type.key, color: type.color })), { key: 'SCHOOL_EVENT', color: 'purple' }];
const OWNER_EVERYONE = '';
const OWNER_ME = 'ME';

/** @param {string | null} value */
function readView(value) {
  return /** @type {'month' | 'week' | 'day'} */ (CALENDAR_VIEWS.includes(/** @type {any} */ (value)) ? value : 'week');
}

/** @param {string | null} value */
function readAnchor(value) {
  if (value && /^\d{4}-\d{2}-\d{2}$/.test(value)) return fromDateKey(value);
  return new Date();
}

export function CalendarPage() {
  const parameters = searchParamsState.value;
  const view = readView(parameters.get('view'));
  const anchor = readAnchor(parameters.get('date'));
  const owner = parameters.get('owner') ?? OWNER_EVERYONE;
  const hiddenTypes = new Set((parameters.get('hide') ?? '').split(',').filter(Boolean));
  const includeCanceled = parameters.get('canceled') === '1';
  const [items, setItems] = useState(/** @type {CalendarItem[] | null} */ (null));
  const [selected, setSelected] = useState(/** @type {CalendarItem | null} */ (null));
  // startsAt null: o diálogo sugere a próxima hora cheia
  const [createAt, setCreateAt] = useState(/** @type {{ startsAt: string | null } | null} */ (null));
  const [reloadKey, setReloadKey] = useState(0);
  const requestRef = useRef(0);
  const range = useMemo(() => getVisibleRange(view, anchor), [view, toDateKey(anchor)]);
  const members = workspaceMembersState.value ?? [];

  useEffect(() => {
    document.title = t('calendar.title');
    loadWorkspaceMembers();
  }, []);

  useEffect(() => {
    const requestId = (requestRef.current += 1);
    listCalendarEvents({
      startsAt: range.start.toISOString(),
      endsAt: range.end.toISOString(),
      workspaceMemberId: owner || null,
      includeCanceled,
    })
      .then((loaded) => {
        if (requestId !== requestRef.current) return;
        setItems(loaded);
        // o item aberto é atualizado com a versão nova (remarcação, cancelamento)
        setSelected((current) => (current ? loaded.find((item) => item.id === current.id) ?? null : null));
      })
      .catch((error) => {
        if (requestId !== requestRef.current) return;
        showErrorSnackbar(error);
        setItems([]);
      });
  }, [range.start.getTime(), range.end.getTime(), owner, includeCanceled, reloadKey]);

  const visibleItems = (items ?? []).filter((item) => !hiddenTypes.has(item.kind === 'SCHOOL_EVENT' ? 'SCHOOL_EVENT' : item.eventType));
  const reload = () => setReloadKey((value) => value + 1);
  /** @param {Date} date */
  const goToDate = (date) => updateSearchParams({ date: toDateKey(date) });
  /** @param {Date} day */
  const openDay = (day) => updateSearchParams({ view: 'day', date: toDateKey(day) });
  /** @param {string} typeKey */
  const toggleType = (typeKey) => {
    const next = new Set(hiddenTypes);
    if (next.has(typeKey)) next.delete(typeKey);
    else next.add(typeKey);
    updateSearchParams({ hide: next.size > 0 ? [...next].join(',') : null });
  };

  const toolbar = html`<div class="calendar-toolbar">
    <div class="calendar-toolbar-navigation">
      <${Button} title=${t('calendar.today')} variant="secondary" size="small" onClick=${() => goToDate(new Date())} />
      <${LightIconButton} icon="IconChevronLeft" ariaLabel=${t('calendar.previous')} onClick=${() => goToDate(shiftAnchor(view, anchor, -1))} />
      <${LightIconButton} icon="IconChevronRight" ariaLabel=${t('calendar.next')} onClick=${() => goToDate(shiftAnchor(view, anchor, 1))} />
      <h2 class="calendar-toolbar-title" data-testid="calendar-range-title">${formatRangeTitle(view, anchor)}</h2>
    </div>
    <div class="calendar-toolbar-filters">
      <label class="calendar-owner-filter">
        <${Icon} name="IconUserCircle" size=${14} />
        <select
          class="calendar-owner-select"
          aria-label=${t('calendar.filters.owner')}
          value=${owner}
          onChange=${(event) => updateSearchParams({ owner: /** @type {HTMLSelectElement} */ (event.currentTarget).value || null })}
        >
          <option value=${OWNER_EVERYONE}>${t('calendar.filters.everyone')}</option>
          <option value=${OWNER_ME}>${t('calendar.filters.mine')}</option>
          ${members.map((member) => html`<option key=${member.id} value=${member.id}>${formatFullName(member.name) || member.userEmail}</option>`)}
        </select>
      </label>
      <div class="segmented-control calendar-view-switch" role="tablist" aria-label=${t('calendar.title')}>
        ${CALENDAR_VIEWS.map((option) => html`<button
          type="button"
          key=${option}
          role="tab"
          class="segmented-item"
          aria-selected=${view === option ? 'true' : 'false'}
          data-active=${view === option ? 'true' : undefined}
          onClick=${() => updateSearchParams({ view: option })}
        >${t(`calendar.views.${option}`)}</button>`)}
      </div>
    </div>
  </div>`;

  const legend = html`<div class="calendar-legend" aria-label=${t('calendar.filters.eventTypes')}>
    ${LEGEND_TYPES.map((type) => html`<button
      type="button"
      key=${type.key}
      class="calendar-legend-item"
      aria-pressed=${hiddenTypes.has(type.key) ? 'false' : 'true'}
      data-hidden=${hiddenTypes.has(type.key) ? 'true' : undefined}
      onClick=${() => toggleType(type.key)}
    >
      <span class="calendar-legend-dot" style=${{ background: getTagStyle(type.color).color }}></span>
      ${t(`calendar.eventTypes.${type.key}`)}
    </button>`)}
    <span class="calendar-legend-spacer"></span>
    <label class="calendar-legend-toggle">
      <${Toggle} size="small" value=${includeCanceled} onChange=${(value) => updateSearchParams({ canceled: value ? '1' : null })} ariaLabel=${t('calendar.filters.showCanceled')} />
      <span>${t('calendar.filters.showCanceled')}</span>
    </label>
  </div>`;

  return html`<${PageCard}
    className="calendar-page-card"
    header=${html`<${PageHeader}
      icon="IconCalendar"
      title=${t('calendar.title')}
      actions=${html`<${Button} title=${t('calendar.newEvent')} icon="IconPlus" variant="secondary" size="small" onClick=${() => setCreateAt({ startsAt: null })} />`}
    />`}
  >
    <div class="calendar-page" data-testid="calendar-page">
      ${toolbar}
      ${legend}
      <div class="calendar-body" data-loading=${items === null ? 'true' : undefined}>
        ${view === 'month'
          ? html`<${MonthView} anchor=${anchor} items=${visibleItems} selectedId=${selected?.id ?? null} onSelect=${setSelected} onOpenDay=${openDay} />`
          : html`<${TimeGridView}
              view=${view}
              anchor=${anchor}
              items=${visibleItems}
              selectedId=${selected?.id ?? null}
              onSelect=${setSelected}
              onOpenDay=${openDay}
              onCreateAt=${(startsAt) => setCreateAt({ startsAt: startsAt.toISOString() })}
            />`}
        ${items !== null && visibleItems.length === 0 ? html`<div class="calendar-empty">${t('calendar.empty')}</div>` : null}
        ${selected ? html`<${CalendarEventDetail} key=${selected.id} item=${selected} onClose=${() => setSelected(null)} onChanged=${reload} />` : null}
      </div>
    </div>
    ${createAt
      ? html`<${ScheduleEventModal} defaultStartsAt=${createAt.startsAt} onClose=${() => setCreateAt(null)} onSaved=${reload} />`
      : null}
  <//>`;
}
